import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { DashedSeparator } from '~/commonComponents';
import { IReferrer, ReferralStatus } from '~/domain/Referral';
import { IReferral, ReferralListStatuses, UserTypeEnum } from '~/models';
import { EmptyReferral } from './EmptyReferral';

import HourglassIcon from '~/images/hourglass.svg';

import MemoCircleCheckIcon from '~/images/memo-circle-check.svg';
import { SolidBriefcaseIcon, SolidCalendarCheckIcon, SolidCurrencyDollarIcon, SolidHomeHouseIcon, SolidUserIcon } from '@tempmee/react-icons';

interface IReferralListProps {
  data: IReferral[];
  style?: React.CSSProperties;
  referrer: IReferrer | null;
}

export const ReferralList = ({ data = [], referrer, style }: IReferralListProps) => {
  const showReferredMeSection = !!referrer;

  const referralStatusIconMap: Record<ReferralListStatuses, React.ReactNode> = {
    [ReferralListStatuses.AccountCreated]: <img src={MemoCircleCheckIcon} alt="icon" style={{ width: 19, height: 19 }} />,
    [ReferralListStatuses.FirstShiftScheduled]: (
      <SolidCalendarCheckIcon size={19} color={colors.secondary[500]} />
    ),
    [ReferralListStatuses.FirstShiftCompleted]: (
      <SolidBriefcaseIcon size={19} color={colors.secondary[500]} />
    ),
    [ReferralListStatuses.PaymentSent]: (
      <SolidCurrencyDollarIcon size={19} color={colors.secondary[500]} />
    ),
  };

  const referralStatusOrder = [
    ReferralListStatuses.AccountCreated,
    ReferralListStatuses.FirstShiftScheduled,
    ReferralListStatuses.FirstShiftCompleted,
    ReferralListStatuses.PaymentSent,
  ];

  const renderStatusIcon = (status: ReferralListStatuses) => {
    const icon = referralStatusIconMap[status];
    return icon ? <span style={{ marginLeft: 8, display: 'flex' }}>{icon}</span> : null;
  };

  const getReferralStatusText = (status: ReferralListStatuses) => {
    return status.replace('First', '1st');
  };

  const orderedData = data.sort((a, b) => {
    return (
      referralStatusOrder.indexOf(a.referralStatus) - referralStatusOrder.indexOf(b.referralStatus)
    );
  });

  return (
    <Column style={{ width: 549, ...style }}>
      {(showReferredMeSection || data?.length > 0) && (
        <Typography variant="body1"
          italic
          color={colors.neutral[500]}
          style={{ textAlign: 'center', marginTop: 24, marginBottom: 8 }}
        >
          Track their progress step by step here: Account Created, Shift Scheduled,
          <br />
          Shift Completed, Payment Sent.
        </Typography>
      )}

      {showReferredMeSection && (
        <Column style={{ marginTop: 16 }}>
          <Row align="center" gap={3} pb={5}>
            <DashedSeparator width={180} />
            <Typography as="span" bold variant="body1" color={colors.primary[500]}>
              Who Referred Me
            </Typography>
            <DashedSeparator width={180} />
          </Row>

          <Row align="center" justify="between">
            <Row align="center">
              <SolidUserIcon size={24} color={colors.secondary[500]} />
              <Typography as="span" bold numberOfLines={1} style={{ maxWidth: 320 }}>
                {referrer.name}
              </Typography>
            </Row>

            <Row align="center">
              <Typography as="span" variant="body1" color={colors.neutral[500]}>
                {referrer.status === ReferralStatus.Paid ? 'Payment Sent' : 'In Progress'}
              </Typography>
              {referrer.status === ReferralStatus.Paid ? (
                <SolidCurrencyDollarIcon size={19} color={colors.secondary[500]} />
              ) : (
                <img src={HourglassIcon} alt="icon" style={{ width: 19, height: 19, marginLeft: 5 }} />
              )}
            </Row>
          </Row>
        </Column>
      )}

      {showReferredMeSection && (
        <Column
          align="center"
          justify="center"
          style={{ alignSelf: 'center', width: '100%', marginTop: 32 }}
        >
          <Row align="center" gap={3}>
            <DashedSeparator width={205} />
            <Typography as="span" bold variant="body1" color={colors.primary[500]}>
              My Referrals
            </Typography>
            <DashedSeparator width={205} />
          </Row>
        </Column>
      )}

      {data?.length === 0 && <EmptyReferral />}

      {data?.length > 0 && (
        <>
          {orderedData.map((item, idx) => (
            <Row
              key={item.id}
              pt={6}
              pb={5}
              align="center"
              justify="between"
              style={{
                borderBottom: idx === data.length - 1 ? 'none' : '1px solid #EDEDED',
              }}
            >
              <Row align="center">
                <span style={{ marginRight: 5, display: 'flex' }}>
                  {item.referredUserType === UserTypeEnum.DNT ? (
                    <SolidHomeHouseIcon size={24} color={colors.secondary[500]} />
                  ) : (
                    <SolidUserIcon size={24} color={colors.secondary[500]} />
                  )}
                </span>
                <Typography as="span" bold>{item.referredName}</Typography>
              </Row>

              <Row align="center">
                <Typography as="span" color={colors.neutral[500]}>{getReferralStatusText(item.referralStatus)}</Typography>
                {renderStatusIcon(item.referralStatus)}
              </Row>
            </Row>
          ))}
        </>
      )}
    </Column>
  );
};
